/* SUMMARY
  * Imports
  * NoteField: one labelled note row
  * NotesSection: the intervention's three notes, gathered in one place
*/

/* Imports */
import { Card, Typography } from "@material-tailwind/react";
import { isBlankNote } from "../../../../../components/data/customer/CustomerNoteCallout";
import { t } from "i18next";
/***/

interface NotesSectionProps {
  workorder: EasyTech.Workorder;
}

/**
 * NoteField: one note, always rendered.
 *
 * Unlike on the Général grid -- where an empty note used to be omitted so it would not read as a
 * labelled blank among unrelated fields -- the three rows here are the whole point of the section.
 * Dropping the empty ones would leave the reader unable to tell "this customer has no technician
 * note" from "this build does not show technician notes", so a dash says it explicitly. It matches
 * the customer profile, where the same two notes are also shown with a dash when unset.
 *
 * whitespace-pre-line because these are written as checklists, one instruction per line; break-words
 * because nothing guarantees a space in 1000 characters of free text.
 */
function NoteField({ label, note }: { label: string, note?: string }) {
  return (
    <div className="field col-span-2">
      <label>{label}</label>
      <Typography className="whitespace-pre-line break-words">
        {isBlankNote(note) ? "-" : note}
      </Typography>
    </div>
  );
}

/**
 * NotesSection: general note, technician note and planner note, in one section of their own.
 *
 * They used to be scattered: the intervention's own note sat at the bottom of the on-call block,
 * the planner note hung off the customer field, and the technician note was not shown at all. A
 * reader looking for "what was said about this job" had to know where each one lived.
 *
 * Only the general note belongs to the intervention (workorder.notes). The other two belong to the
 * customer and are shown here read-only -- editing one on an intervention would change it for every
 * intervention of that customer, which is the customer form's job. They arrive on
 * CustomerSimpleResponse, so grouping them costs no extra request.
 *
 * The planner note is also pinned as a post-it above the tabs (CustomerNoteSticky in WorkorderTabs).
 * The duplication is deliberate: the post-it is the attention device, this row is the record, and a
 * reader who comes here for "what was said about this job" must not find a hole where one note is.
 *
 * @param workorder - the intervention
 */
export function NotesSection({ workorder }: NotesSectionProps) {
  return (
    <>
      <div className="information-header">
        <Typography variant="h4">{t("workorder:notes:title")}</Typography>
      </div>

      <Card className="information-content">
        <NoteField label={t("workorder:notes:general")} note={workorder?.notes} />

        {/*
          * The two customer notes keep the labels the customer form and profile give them, so a
          * planner reading "Note technicien" here and there is reading about the same field.
          */}
        <NoteField
          label={t("client:form:fields:technicianNote:label")}
          note={workorder?.customer?.technicianNote}
        />

        <NoteField
          label={t("client:form:fields:plannerNote:label")}
          note={workorder?.customer?.plannerNote}
        />
      </Card>
    </>
  );
}
/***/
